import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Landmark, BellOff, ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";

// Jun 2026 · Shared "due this week" nudge banners (Hub + Weekly Review).
// Mirrors the 7-day email reminder window. Snoozing mutes both the
// banner and the daily reminder email for that business/kind.

const WINDOW_DAYS = 7;

const _window = () => {
  const today = new Date().toISOString().slice(0, 10);
  const week = new Date(Date.now() + WINDOW_DAYS * 86400000).toISOString().slice(0, 10);
  return [today, week];
};

export const complianceDueSoonItems = (business) => {
  const [today, week] = _window();
  return (business?.compliance?.items || []).filter(
    (it) =>
      it?.due_date &&
      !["completed", "not_applicable"].includes(it.status) &&
      it.due_date >= today &&
      it.due_date <= week
  );
};

// Jun 2026 · Overdue escalation — open items whose due date already
// slipped past. The banner turns red and leads with these.
export const complianceOverdueItems = (business) => {
  const [today] = _window();
  return (business?.compliance?.items || []).filter(
    (it) =>
      it?.due_date &&
      !["completed", "not_applicable"].includes(it.status) &&
      it.due_date < today
  );
};

export const grantsDueSoonItems = (business) => {
  const [today, week] = _window();
  return (business?.grants || []).filter(
    (g) =>
      g?.deadline &&
      ["saved", "drafting"].includes(g.status) &&
      g.deadline >= today &&
      g.deadline <= week
  );
};

export const isReminderSnoozed = (business, kind) => {
  const until = business?.reminder_snooze?.[kind];
  return !!until && new Date().toISOString().slice(0, 10) <= until;
};

// Jun 2026 · What's-due badge count — overdue + due-this-week open
// compliance items, shared by the Launch and Operating command bars.
export const dueAttentionCount = (business) =>
  complianceOverdueItems(business).length + complianceDueSoonItems(business).length;

// Jun 2026 · Roadmap nudges — open roadmap tasks due this week / overdue
// so the What's Due panel can push their dates without leaving the page.
export const roadmapDueSoonItems = (business) => {
  const [today, week] = _window();
  return (business?.roadmap || []).filter(
    (it) =>
      it?.completion_date &&
      !["done", "completed"].includes(it.status) &&
      it.completion_date >= today &&
      it.completion_date <= week
  );
};

export const roadmapOverdueItems = (business) => {
  const [today] = _window();
  return (business?.roadmap || []).filter(
    (it) =>
      it?.completion_date &&
      !["done", "completed"].includes(it.status) &&
      it.completion_date < today
  );
};

export const roadmapDueAttentionCount = (business) =>
  roadmapOverdueItems(business).length + roadmapDueSoonItems(business).length;

// Jun 2026 · Grants closing this week (saved/drafting only) — counted
// into the What's Due badge so the panel's Grants section is reachable.
export const grantsDueAttentionCount = (business) =>
  grantsDueSoonItems(business).length;

const KIND_CONFIG = {
  compliance: {
    icon: CalendarClock,
    accent: "#D97706",
    border: "border-[#D97706]",
    bg: "bg-[#FFFBEB] hover:bg-[#FEF3C7]",
    text: "text-[#D97706]",
    link: (id) => `/compliance?business=${id}`,
    label: (n) => `${n} compliance item${n === 1 ? " is" : "s are"} due this week`,
    cta: "Open tracker",
    getItems: complianceDueSoonItems,
    getDate: (it) => it.due_date,
  },
  grants: {
    icon: Landmark,
    accent: "#059669",
    border: "border-[#059669]",
    bg: "bg-[#ECFDF5] hover:bg-[#D1FAE5]",
    text: "text-[#059669]",
    link: (id) => `/grants?business=${id}`,
    label: (n) => `${n} grant deadline${n === 1 ? " lands" : "s land"} this week`,
    cta: "Open grants",
    getItems: grantsDueSoonItems,
    getDate: (g) => g.deadline,
  },
};

export function DueSoonBanner({ business, kind = "compliance", testid, className = "", onBusinessUpdate }) {
  const [snoozing, setSnoozing] = useState(false);
  const cfg = KIND_CONFIG[kind];
  const items = cfg.getItems(business);
  // Jun 2026 · Overdue escalation (compliance only) — red treatment,
  // overdue-first wording.
  const overdue = kind === "compliance" ? complianceOverdueItems(business) : [];
  const escalated = overdue.length > 0;
  if ((!items.length && !overdue.length) || isReminderSnoozed(business, kind)) return null;
  const earliest = items.map(cfg.getDate).sort()[0];
  const oldestOverdue = overdue.map(cfg.getDate).sort()[0];
  const Icon = escalated ? AlertTriangle : cfg.icon;
  const border = escalated ? "border-[#DC2626]" : cfg.border;
  const bg = escalated ? "bg-[#FEF2F2] hover:bg-[#FEE2E2]" : cfg.bg;
  const accentText = escalated ? "text-[#DC2626]" : cfg.text;
  const label = escalated
    ? `${overdue.length} compliance item${overdue.length === 1 ? " is" : "s are"} overdue`
    : cfg.label(items.length);
  const detail = escalated
    ? items.length
      ? `plus ${items.length} more due this week`
      : `oldest: ${oldestOverdue}`
    : `earliest: ${earliest}`;

  const snooze = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSnoozing(true);
    try {
      const { data } = await api.post(`/businesses/${business.id}/reminder-snooze`, { kind, days: 3 });
      toast.success(`Snoozed until ${data.snoozed_until} — banner and email nudges paused.`);
      onBusinessUpdate?.({
        ...business,
        reminder_snooze: { ...(business.reminder_snooze || {}), [kind]: data.snoozed_until },
      });
    } catch {
      toast.error("Couldn't snooze — try again.");
    } finally {
      setSnoozing(false);
    }
  };

  return (
    <Link
      to={cfg.link(business.id)}
      data-testid={testid}
      data-overdue={escalated ? "true" : undefined}
      className={`flex items-center gap-3 border ${border} ${bg} px-4 py-3 transition-colors group ${className}`}
    >
      <Icon className={`w-4 h-4 ${accentText} flex-shrink-0`} />
      <span className="text-sm text-neutral-800 min-w-0 truncate">
        <b className={escalated ? "text-[#DC2626]" : ""}>{label}</b>
        <span className="text-neutral-500"> — {detail}</span>
      </span>
      <span className="ml-auto flex items-center gap-3 flex-shrink-0">
        <button
          type="button"
          onClick={snooze}
          disabled={snoozing}
          data-testid={`${testid}-snooze-btn`}
          title="Pause this nudge (and the email reminders) for 3 days"
          className="text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-400 hover:text-neutral-900 flex items-center gap-1"
        >
          {snoozing ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellOff className="w-3 h-3" />} Snooze 3d
        </button>
        <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${accentText} flex items-center gap-1`}>
          {cfg.cta} <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </span>
    </Link>
  );
}
